/**
 * Pure permission checks over a PermissionSnapshot. The auth store, the
 * router guard, navigation, `v-can` and `<Can>` all go through these, so the
 * UI makes one decision the same way everywhere.
 *
 * Rules:
 *  - Staff only: a client or super-admin session holds no staff permission.
 *  - Fail closed: no principal, no permission data, an unknown slug or a
 *    malformed scope → no access.
 *  - `permissionScopes` is the authority once present (the server already
 *    gives owners / full-access roles every entitled slug and removes
 *    disabled modules), so there is no owner bypass.
 *  - Legacy cached session (no `permissionScopes` yet): the `permissions`
 *    array decides; the owner keeps the old bypass at scope 'all'; for
 *    everyone else the scope is unknown, so a minimum-scope check fails and
 *    scopeOf() is null until the next refresh brings the scopes.
 */
import { atLeast, isScope } from './scopes';
import { isPermissionSlug, type PermissionSlug } from './slugs';
import type { PermissionRequirement, PermissionSnapshot, Principal, Scope } from './types';

const UNKNOWN_SCOPE = 'unknown' as const;
type Grant = Scope | typeof UNKNOWN_SCOPE | null;

/** The permission fields of a signed-in user (stores/auth.ts User). */
export interface PermissionCarrier {
  permissions?: readonly string[] | null;
  permissionScopes?: Readonly<Record<string, string | undefined>> | null;
  isOwner?: boolean | null;
  fullAccess?: boolean | null;
}

export function snapshotFromUser(principal: Principal | null, user: PermissionCarrier | null | undefined): PermissionSnapshot {
  if (!user) return { principal: null };
  return {
    principal,
    permissions: user.permissions,
    permissionScopes: user.permissionScopes,
    isOwner: user.isOwner,
    fullAccess: user.fullAccess,
  };
}

/** Owner or full-access role. Legacy cached sessions: the owner. */
export function hasFullAccess(s: PermissionSnapshot): boolean {
  if (s.principal !== 'staff') return false;
  if (s.fullAccess === true) return true;
  return isLegacySnapshot(s) && s.isOwner === true;
}

export function isLegacySnapshot(s: PermissionSnapshot): boolean {
  return s.principal === 'staff' && (s.permissionScopes === undefined || s.permissionScopes === null);
}

function grantOf(s: PermissionSnapshot, slug: PermissionSlug): Grant {
  if (s.principal !== 'staff' || !isPermissionSlug(slug)) return null;
  if (!isLegacySnapshot(s)) {
    const scope = s.permissionScopes?.[slug];
    return isScope(scope) ? scope : null;
  }
  if (s.isOwner === true) return 'all';
  return Array.isArray(s.permissions) && s.permissions.includes(slug) ? UNKNOWN_SCOPE : null;
}

/** The scope granted for `slug`, or null (not granted, or not provable on a legacy session). */
export function scopeOf(s: PermissionSnapshot, slug: PermissionSlug): Scope | null {
  const grant = grantOf(s, slug);
  return grant === UNKNOWN_SCOPE ? null : grant;
}

export function can(s: PermissionSnapshot, slug: PermissionSlug, minScope?: Scope): boolean {
  const grant = grantOf(s, slug);
  if (grant === null) return false;
  if (grant === UNKNOWN_SCOPE) return !minScope;
  return atLeast(grant, minScope);
}

export function canAny(s: PermissionSnapshot, slugs: readonly PermissionSlug[], minScope?: Scope): boolean {
  return slugs.some((slug) => can(s, slug, minScope));
}

export function canAll(s: PermissionSnapshot, slugs: readonly PermissionSlug[], minScope?: Scope): boolean {
  return slugs.length > 0 && slugs.every((slug) => can(s, slug, minScope));
}

/** Does the session meet a requirement (slug | {anyOf} | {allOf} | {slug, scope} | {any} | slug[])? Missing → no. */
export function meets(s: PermissionSnapshot, req: PermissionRequirement | readonly PermissionSlug[] | null | undefined): boolean {
  if (!req) return false;
  if (typeof req === 'string') return can(s, req);
  if (Array.isArray(req)) return canAny(s, req as readonly PermissionSlug[]);
  const r = req as Partial<{ anyOf: readonly PermissionSlug[]; allOf: readonly PermissionSlug[]; slug: PermissionSlug; scope: Scope; any: readonly PermissionRequirement[] }>;
  if (Array.isArray(r.any)) return r.any.some((branch) => meets(s, branch));
  if (Array.isArray(r.anyOf)) return canAny(s, r.anyOf, r.scope);
  if (Array.isArray(r.allOf)) return canAll(s, r.allOf, r.scope);
  if (typeof r.slug === 'string') return can(s, r.slug, r.scope);
  return false;
}

/** Every slug a snapshot grants (scoped sessions: permissionScopes; legacy: the permissions array). */
function grantedSlugs(s: PermissionSnapshot): string[] {
  const slugs = new Set<string>(Array.isArray(s.permissions) ? s.permissions : []);
  if (s.permissionScopes) for (const slug of Object.keys(s.permissionScopes)) slugs.add(slug);
  return [...slugs].filter(isPermissionSlug);
}

/**
 * Did access shrink from `prev` to `next` — a grant removed, a scope lowered
 * (all → own, both → team…), or full access lost? Used to remount the open
 * page after a live permission change (rbac/refresh.ts `permissionsNarrowed`)
 * so company-wide data fetched under the old access leaves the screen.
 * Widening is not narrowing: open forms survive it. A legacy session (scope
 * unknown) narrows only when a slug disappears.
 */
export function isNarrowing(prev: PermissionSnapshot, next: PermissionSnapshot): boolean {
  if (prev.principal !== 'staff') return false;
  if (hasFullAccess(prev) && !hasFullAccess(next)) return true;
  for (const slug of grantedSlugs(prev) as PermissionSlug[]) {
    const before = grantOf(prev, slug);
    if (before === null) continue;
    const after = grantOf(next, slug);
    if (after === null) return true;
    if (before === UNKNOWN_SCOPE || after === UNKNOWN_SCOPE) continue;
    if (!atLeast(after, before)) return true;
  }
  return false;
}
